import { useState, useCallback } from 'react';

interface UseImageDownloadReturn {
  downloadImage: (imageUrl: string, filename: string) => Promise<void>;
  openImageInNewTab: (imageUrl: string) => void;
  isDownloading: boolean;
  error: string | null;
}

/**
 * 画像ダウンロード用カスタムフック
 * - Base64データURLとHTTP URLの両方に対応
 * - ダウンロード中の状態管理
 * - エラーハンドリング
 * - CORS対応
 * - 新しいタブで画像表示（スマホでの写真保存用）
 */
export const useImageDownload = (): UseImageDownloadReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadImage = useCallback(async (imageUrl: string, filename: string) => {
    if (!imageUrl || !filename) {
      setError('画像URLまたはファイル名が指定されていません');
      return;
    }

    setIsDownloading(true);
    setError(null);

    try {
      let downloadUrl: string;
      let shouldRevoke = false;

      if (imageUrl.startsWith('data:image/')) {
        // Base64データURLの場合はそのまま使用
        downloadUrl = imageUrl;
      } else {
        // HTTP URLの場合はCORS対応のため fetch を使用
        const response = await fetch(imageUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        // モバイルデバイスでの写真フォルダ保存のためMIMEタイプを明示的に指定
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile && filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/)) {
          // スマホの場合は画像MIMEタイプを明示的に設定
          const imageType = filename.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
          const typedBlob = new Blob([blob], { type: imageType });
          downloadUrl = window.URL.createObjectURL(typedBlob);
        } else {
          // PC版では従来通りの仕様を維持
          downloadUrl = window.URL.createObjectURL(blob);
        }
        shouldRevoke = true;
      }

      // ダウンロード実行
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // メモリ解放（HTTP URLから作成したBlobURLの場合のみ）
      if (shouldRevoke) {
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '画像のダウンロードに失敗しました';
      setError(errorMessage);
      console.error('画像のダウンロードに失敗しました:', err);
      
      // ユーザーフレンドリーなエラー通知
      alert('画像のダウンロードに失敗しました。再度お試しください。');
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const openImageInNewTab = useCallback((imageUrl: string) => {
    if (!imageUrl) {
      setError('画像URLが指定されていません');
      return;
    }
    
    // 新しいタブで画像を開く（スマホでの写真保存用）
    window.open(imageUrl, '_blank');
  }, []);

  return {
    downloadImage,
    openImageInNewTab,
    isDownloading,
    error
  };
};export default useImageDownload;
